import type { ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'
import { SectionCard, formatDate } from '@datahub/design-system'
import {
  tabsForEntity, allSectionFields, visibleSectionFields, taxonomies, type EntityType, type FieldDef, type SectionDef,
} from '@datahub/shared'
import { get } from '../../lib/api'
import { ExternalLink } from '../../components/ExternalLink'
import { TICKET_SIZE_CURRENCY_FIELD, withCurrency } from '../ticket-size'

interface ProfileRow {
  id: string
  values: Record<string, unknown>
  confirmationState?: string
  preloadedRowId?: string | null
  providedByStears?: boolean
}

interface ProfileTabState {
  values: Record<string, unknown>
  rows: Record<string, ProfileRow[]>
  controls: Record<string, unknown>
}

export interface CompanyProfileResponse {
  profile: {
    payload: { tabs: Record<string, ProfileTabState> }
    hasSubmission: boolean
    lastSubmittedAt: string | null
    lastSubmittedByName: string | null
    /** Paths still standing on the Stears seed, i.e. not yet answered by the company. */
    seededPaths: string[]
  } | null
}

function isBlank(value: unknown): boolean {
  if (value === null || value === undefined || value === '') return true
  if (Array.isArray(value)) return value.length === 0
  return false
}

/** Turns a stored slug back into the name the taxonomy shows, so the admin reads "Nigeria", not "nigeria". */
function labelFor(field: FieldDef | undefined, value: string): string {
  if (!field?.taxonomy) return value
  return (taxonomies[field.taxonomy] ?? []).find((t) => t.slug === value)?.name ?? value
}

function formatValue(field: FieldDef | undefined, value: unknown, currency?: unknown): string {
  if (isBlank(value)) return '-'
  if (Array.isArray(value)) {
    const items = value.map((item) =>
      item && typeof item === 'object' && 'name' in item
        ? String((item as { name: string }).name)
        : labelFor(field, String(item)),
    )
    // A ticket size band shows with the currency its section or row gives it (F6).
    const sized = field && TICKET_SIZE_CURRENCY_FIELD[field.key] && typeof currency === 'string'
      ? withCurrency(items, currency)
      : items
    return sized.join(', ')
  }
  if (typeof value === 'object') {
    const o = value as { amount?: number | null; currency?: string; undisclosed?: boolean; value?: string }
    if (o.undisclosed) return 'Undisclosed'
    if (typeof o.amount === 'number') return `${o.currency ?? ''} ${o.amount.toLocaleString('en-GB')}`.trim()
    if (o.value) return o.value
    return JSON.stringify(value)
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  return labelFor(field, String(value))
}

/**
 * The same value, ready to render: a web address becomes a link the admin can follow,
 * everything else stays the text `formatValue` produced. `values` are the other answers of
 * the same section or row, where a ticket size finds its currency.
 */
function renderValue(field: FieldDef | undefined, value: unknown, values: Record<string, unknown> = {}): ReactNode {
  if (field?.input === 'url' && !isBlank(value)) return <ExternalLink value={value} />
  const currencyKey = field ? TICKET_SIZE_CURRENCY_FIELD[field.key] : undefined
  return formatValue(field, value, currencyKey ? values[currencyKey] : undefined)
}

/** The heading a repeatable entry is listed under, from the section's own template. */
function entryTitle(section: SectionDef, row: ProfileRow): string {
  const template = section.rowLabel ?? '{name}'
  const filled = template.replace(/\{([^}]+)\}/g, (_, key: string) => {
    const field = allSectionFields(section).find((f) => f.key === key || f.key === `datahub.${key}`)
    const value = row.values[key] ?? row.values[`datahub.${key}`]
    return isBlank(value) ? '' : formatValue(field, value)
  })
  return filled.replace(/\s*—\s*$/, '').replace(/^\s*—\s*/, '').trim() || 'Untitled entry'
}

/** Marks a value the company has not answered yet, so it is still Stears' own. */
function SeedTag() {
  return (
    <span
      className="font-mulish text-text-support flex-shrink-0"
      style={{ fontSize: 10, lineHeight: '14px', border: '0.5px solid #4B5563', padding: '0 5px', marginLeft: 8 }}
      title="From Stears. The company has not confirmed or changed it yet."
    >
      Stears
    </span>
  )
}

interface Entry {
  label: string
  value: ReactNode
  seeded: boolean
}

function KeyValues({ entries }: { entries: Entry[] }) {
  return (
    <div className="flex flex-col">
      {entries.map(({ label, value, seeded }) => (
        <div key={label} className="flex items-start" style={{ padding: '8px 20px', borderBottom: '0.5px solid #4B5563' }}>
          <span className="font-mulish text-white flex-shrink-0" style={{ fontSize: 12, width: '42%' }}>{label}</span>
          <span className="font-mulish text-white flex items-start" style={{ fontSize: 12, flex: 1 }}>
            <span style={{ flex: 1 }}>{value}</span>
            {seeded && <SeedTag />}
          </span>
        </div>
      ))}
    </div>
  )
}

/** The answers of one fields section, keyed by field key as a row keys its values. */
function sectionValues(values: Record<string, unknown>, sectionKey: string): Record<string, unknown> {
  const prefix = `${sectionKey}.`
  return Object.fromEntries(
    Object.entries(values)
      .filter(([key]) => key.startsWith(prefix))
      .map(([key, value]) => [key.slice(prefix.length), value]),
  )
}

/** Tables that list every field of a row, also after a submission: all fund vehicle attributes (F7). */
const EVERY_FIELD_SECTIONS = new Set(['fund_vehicle'])

/**
 * The table of one repeatable section: one entry per row, with its fields under it. The
 * profile and the pre-loaded rows use the same table. `everyField` lists every field the
 * form shows for that row, with a dash for a blank one, so the admin sees each attribute the
 * form holds and not only the answered ones.
 */
function RowsCard({
  section,
  rows,
  tag,
  everyField = false,
}: {
  section: SectionDef
  rows: ProfileRow[]
  tag: (row: ProfileRow) => ReactNode
  everyField?: boolean
}) {
  return (
    <SectionCard title={`${section.label} (${rows.length})`}>
      <div className="flex flex-col">
        {rows.map((row) => {
          const fields = everyField || EVERY_FIELD_SECTIONS.has(section.rowEntity ?? '')
            ? visibleSectionFields(section, { values: row.values })
            : allSectionFields(section).filter((field) => !isBlank(row.values[field.key]))
          return (
            <div key={row.id} style={{ borderBottom: '0.5px solid #4B5563', padding: '12px 20px' }}>
              <p className="font-mulish font-bold text-white flex items-center" style={{ fontSize: 12, marginBottom: 8 }}>
                {entryTitle(section, row)}
                {tag(row)}
              </p>
              <div className="grid grid-cols-2 gap-x-5">
                {fields.map((field) => (
                  <div key={field.key} className="flex" style={{ padding: '3px 0', gap: 8 }}>
                    <span className="font-mulish text-text-support" style={{ fontSize: 12, width: '45%' }}>{field.label}</span>
                    <span className="font-mulish text-white" style={{ fontSize: 12, flex: 1 }}>
                      {renderValue(field, row.values[field.key], row.values)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )
        })}
      </div>
    </SectionCard>
  )
}

function TabDivider({ label }: { label: string }) {
  return (
    <div className="flex items-center" style={{ gap: 12 }}>
      <span style={{ flex: 1, height: 1, background: '#4B5563' }} />
      <span className="font-mulish text-text-support" style={{ fontSize: 10, lineHeight: '16px', letterSpacing: '0.4px', textTransform: 'uppercase' }}>
        {label}
      </span>
      <span style={{ flex: 1, height: 1, background: '#4B5563' }} />
    </div>
  )
}

/**
 * Everything known about the entity, laid out in the shape of the form it fills in: what
 * it reported, over what Stears already holds. Spec 11.2 asks for the submitted profile
 * here; showing the Stears side underneath means an entity that has not answered yet
 * still renders every section we have data for, rather than one empty note.
 *
 * The form follows the entity kind, so a fund manager's profile reads as Firm, Mandate
 * and Funds rather than as a company's nine tabs.
 */
export function EntityProfilePanel({
  entityId,
  entityType,
  basePath,
}: {
  entityId: string
  entityType: EntityType
  basePath: string
}) {
  const { data, isLoading } = useQuery({
    queryKey: ['entity-profile', entityId],
    queryFn: () => get<CompanyProfileResponse>(`${basePath}/${entityId}/profile`),
  })

  if (isLoading) {
    return <p className="font-mulish text-text-support" style={{ fontSize: 12 }}>Loading the profile…</p>
  }

  const profile = data?.profile
  if (!profile) {
    return (
      <SectionCard title="Profile">
        <p className="font-mulish text-text-support" style={{ fontSize: 12, lineHeight: '22px', padding: '12px 20px' }}>
          Nothing recorded yet. Link it to Stears or wait for its first update, and everything known about it appears
          here.
        </p>
      </SectionCard>
    )
  }

  const payloadTabs = profile.payload?.tabs ?? {}
  const seeded = new Set(profile.seededPaths ?? [])
  const rendered = tabsForEntity(entityType)
    .filter((tab) => tab.key !== 'review')
    .map((tab) => {
      const state = payloadTabs[tab.key]
      if (!state) return null

      const fieldCards = tab.sections
        .filter((section) => section.kind === 'fields')
        .map((section) => {
          const entries: Entry[] = allSectionFields(section)
            .filter((field) => !isBlank(state.values[`${section.key}.${field.key}`]))
            .map((field) => ({
              label: field.label,
              value: renderValue(field, state.values[`${section.key}.${field.key}`], sectionValues(state.values, section.key)),
              seeded: seeded.has(`${tab.key}.${section.key}.${field.key}`),
            }))
          return entries.length > 0 ? { section, entries } : null
        })
        .filter((card): card is { section: SectionDef; entries: Entry[] } => card !== null)

      const rowCards = tab.sections
        .filter((section) => section.kind === 'repeatable')
        .map((section) => {
          const rows = state.rows?.[section.key] ?? []
          return rows.length > 0 ? { section, rows } : null
        })
        .filter((card): card is { section: SectionDef; rows: ProfileRow[] } => card !== null)

      if (fieldCards.length === 0 && rowCards.length === 0) return null

      return (
        <div key={tab.key} className="flex flex-col" style={{ gap: 20 }}>
          <TabDivider label={tab.label} />
          {fieldCards.length > 0 && (
            <div className="grid grid-cols-2 items-start gap-5">
              {fieldCards.map(({ section, entries }) => (
                <SectionCard key={section.key} title={section.label}>
                  <KeyValues entries={entries} />
                </SectionCard>
              ))}
            </div>
          )}
          {rowCards.map(({ section, rows }) => (
            <RowsCard key={section.key} section={section} rows={rows} tag={(row) => (row.providedByStears ? <SeedTag /> : null)} />
          ))}
        </div>
      )
    })
    .filter(Boolean)

  return (
    <div className="flex flex-col" style={{ gap: 20 }}>
      <p className="font-mulish text-text-support" style={{ fontSize: 12, lineHeight: '16px' }}>
        {profile.hasSubmission
          ? `Reported by ${profile.lastSubmittedByName ?? 'the company'}${
              profile.lastSubmittedAt ? ` on ${formatDate(profile.lastSubmittedAt)}` : ''
            }. Fields still marked Stears are ones the company has not answered, so they show what Stears holds.`
          : 'Everything here is what Stears holds, seeding the form the company will see. Its own answers will replace these values in place.'}
      </p>
      {rendered.length > 0 ? (
        rendered
      ) : (
        <SectionCard title="Company profile">
          <p className="font-mulish text-text-support" style={{ fontSize: 12, lineHeight: '22px', padding: '12px 20px' }}>
            Nothing to show against the dictionary yet.
          </p>
        </SectionCard>
      )}
    </div>
  )
}

export interface PreloadedRowsResponse {
  /** The open cycle whose draft holds the rows, or null when no open cycle has any. */
  cycle: { id: string; periodLabel: string } | null
  /** The pre-loaded rows, by tab and then by section. */
  tabs: Record<string, Record<string, ProfileRow[]>>
}

const NOUN: Record<EntityType, string> = { company: 'company', gp: 'fund manager', lp: 'limited partner' }

/**
 * Marks a row Stears pre-loaded that the entity has not submitted yet. A row the nominee
 * changed in the draft says so, because it no longer shows only what Stears holds.
 */
function PreloadTag({ row }: { row: ProfileRow }) {
  const edited = row.confirmationState !== undefined && row.confirmationState !== 'confirmed'
  return (
    <span
      className="font-mulish text-text-support flex-shrink-0"
      style={{ fontSize: 10, lineHeight: '14px', border: '0.5px solid #4B5563', padding: '0 5px', marginLeft: 8, fontWeight: 400 }}
      title={
        edited
          ? 'Stears pre-loaded this row and the nominee changed it. The entity has not submitted it yet.'
          : 'Stears pre-loaded this row. The entity has not submitted it yet.'
      }
    >
      {edited ? 'Pre-loaded, edited by the nominee' : 'Pre-loaded, not yet confirmed'}
    </span>
  )
}

/**
 * The fund vehicles, transactions and people Stears pre-loaded into the open cycle, read
 * from that cycle's draft (F7). Before a submission these rows are not in the profile, so
 * without this panel the admin cannot see them. Each row uses the profile's own table and
 * lists every attribute the form holds for it.
 */
export function PreloadedRowsPanel({
  entityId,
  entityType,
  basePath,
}: {
  entityId: string
  entityType: EntityType
  basePath: string
}) {
  const { data } = useQuery({
    queryKey: ['entity-preloaded', entityId],
    queryFn: () => get<PreloadedRowsResponse>(`${basePath}/${entityId}/preloaded`),
  })
  if (!data?.cycle) return null

  const cards = tabsForEntity(entityType)
    .flatMap((tab) => tab.sections.map((section) => ({ tab, section })))
    .filter(({ section }) => section.kind === 'repeatable')
    .map(({ tab, section }) => ({ key: `${tab.key}.${section.key}`, section, rows: data.tabs[tab.key]?.[section.key] ?? [] }))
    .filter(({ rows }) => rows.length > 0)
  if (cards.length === 0) return null

  return (
    <div className="flex flex-col" style={{ gap: 20 }}>
      <TabDivider label="Pre-loaded rows, not yet submitted" />
      <p className="font-mulish text-text-support" style={{ fontSize: 12, lineHeight: '16px' }}>
        Stears pre-loaded these rows into {`"${data.cycle.periodLabel}"`}. Each row shows the draft as it is now. The {NOUN[entityType]} has
        not submitted them yet, so it can still confirm, change or remove each one.
      </p>
      {cards.map(({ key, section, rows }) => (
        <RowsCard key={key} section={section} rows={rows} tag={(row) => <PreloadTag row={row} />} everyField />
      ))}
    </div>
  )
}
